import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FormaPagamento {
  id: string;
  nome: string;
  descricao?: string;
  tipo: 'pix' | 'boleto-avista' | 'boleto-parcelado' | 'cartao';
  taxa_juros?: number;
  parcelas_max?: number;
  /** Taxa percentual da máquina de cartão, descontada do valor ao gerar recebimento */
  taxa_maquina_cartao?: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export function useFormasPagamento() {
  return useQuery({
    queryKey: ['formas-pagamento'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('formas_pagamento')
        .select('*')
        .order('nome');

      if (error) {
        console.error('Erro ao buscar formas de pagamento:', error);
        throw error;
      }

      return data as FormaPagamento[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useFormasPagamentoAtivas() {
  return useQuery({
    queryKey: ['formas-pagamento-ativas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('formas_pagamento')
        .select('*')
        .eq('ativo', true)
        .order('nome');

      if (error) {
        console.error('Erro ao buscar formas de pagamento ativas:', error);
        throw error;
      }

      return data as FormaPagamento[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCriarFormaPagamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formaPagamentoData: {
      nome: string;
      descricao?: string;
      tipo: 'pix' | 'boleto-avista' | 'boleto-parcelado' | 'cartao';
      taxa_juros?: number;
      parcelas_max?: number;
      taxa_maquina_cartao?: number;
    }) => {
      const { data, error } = await supabase
        .from('formas_pagamento')
        .insert(formaPagamentoData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formas-pagamento'] });
      queryClient.invalidateQueries({ queryKey: ['formas-pagamento-ativas'] });
    }
  });
}

export function useAtualizarFormaPagamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { 
      id: string; 
      nome?: string;
      descricao?: string;
      tipo?: 'pix' | 'boleto-avista' | 'boleto-parcelado' | 'cartao';
      taxa_juros?: number;
      parcelas_max?: number;
      taxa_maquina_cartao?: number;
      ativo?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('formas_pagamento')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formas-pagamento'] });
      queryClient.invalidateQueries({ queryKey: ['formas-pagamento-ativas'] });
    }
  });
}